import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  Divider,
  Button,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { usePreferencesStore } from '../stores/preferencesStore'
import { usePortalStore } from '../stores/portalStore'
import api from '../api/client'

export default function UserSettingsPage() {
  const { t, i18n } = useTranslation()
  const { launchMode, setLaunchMode, themeMode, setThemeMode, language, setLanguage, appIconSize, setAppIconSize } = usePreferencesStore()
  const { config } = usePortalStore()

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [loading2FA, setLoading2FA] = useState(false)
  const [enrolling, setEnrolling] = useState(false)
  const [enrollSecret, setEnrollSecret] = useState('')
  const [enrollOtpauth, setEnrollOtpauth] = useState('')
  const [enrollCode, setEnrollCode] = useState('')
  const [enrollError, setEnrollError] = useState('')
  const [disableConfirm, setDisableConfirm] = useState(false)
  const [message2FA, setMessage2FA] = useState<{ text: string; severity: 'success' | 'error' } | null>(null)

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
    setLanguage(lang as 'en' | 'es' | 'fr' | 'de' | 'it' | 'ca' | 'eu')
  }

  useEffect(() => {
    load2FAStatus()
  }, [])

  const load2FAStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status')
      setTwoFactorEnabled(res.data.enabled)
    } catch {
      // 2FA endpoint might not be available yet
    }
  }

  const handleStartEnroll = async () => {
    setEnrolling(true)
    setEnrollError('')
    setEnrollCode('')
    setMessage2FA(null)
    try {
      const res = await api.post('/auth/2fa/enroll')
      setEnrollSecret(res.data.secret)
      setEnrollOtpauth(res.data.otpauth_url)
    } catch (err: any) {
      setEnrollError(err.response?.data?.detail || t('twoFactor.enrollError'))
    }
  }

  const handleConfirmEnroll = async () => {
    setLoading2FA(true)
    setEnrollError('')
    try {
      await api.post('/auth/2fa/enroll-confirm', { code: enrollCode })
      setEnrolling(false)
      setEnrollSecret('')
      setEnrollOtpauth('')
      setEnrollCode('')
      setTwoFactorEnabled(true)
      setMessage2FA({ text: t('twoFactor.enableSuccess'), severity: 'success' })
    } catch (err: any) {
      setEnrollError(err.response?.data?.detail || t('twoFactor.invalidCode'))
      setEnrollCode('')
    } finally {
      setLoading2FA(false)
    }
  }

  const handleDisable2FA = async () => {
    setLoading2FA(true)
    setMessage2FA(null)
    try {
      await api.delete('/auth/2fa/disable')
      setTwoFactorEnabled(false)
      setDisableConfirm(false)
      setMessage2FA({ text: t('twoFactor.disableSuccess'), severity: 'success' })
    } catch (err: any) {
      setMessage2FA({ text: err.response?.data?.detail || t('twoFactor.disableError'), severity: 'error' })
    } finally {
      setLoading2FA(false)
    }
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('settings.title')}</Typography>

      <Paper sx={{ p: 3, maxWidth: 600, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('settings.launchMode')}</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <RadioGroup
            value={launchMode}
            onChange={(e) => setLaunchMode(e.target.value as 'native' | 'html5')}
          >
            <FormControlLabel
              value="native"
              control={<Radio />}
              label={t('settings.launchModeNative')}
            />
            <FormControlLabel
              value="html5"
              control={<Radio />}
              label={t('settings.launchModeHtml5')}
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>{t('settings.language')}</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('settings.language')}</InputLabel>
          <Select
            value={language}
            label={t('settings.language')}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            <MenuItem value="es">Espanol</MenuItem>
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="fr">Français</MenuItem>
            <MenuItem value="de">Deutsch</MenuItem>
            <MenuItem value="it">Italiano</MenuItem>
            <MenuItem value="ca">Català</MenuItem>
            <MenuItem value="eu">Euskara</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>{t('settings.theme')}</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('settings.theme')}</InputLabel>
          <Select
            value={themeMode}
            label={t('settings.theme')}
            onChange={(e) => setThemeMode(e.target.value as 'light' | 'dark' | 'system')}
          >
            <MenuItem value="light">{t('settings.themeLight')}</MenuItem>
            <MenuItem value="dark">{t('settings.themeDark')}</MenuItem>
            <MenuItem value="system">{t('settings.themeSystem')}</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>{t('settings.appIconSize')}</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('settings.appIconSize')}</InputLabel>
          <Select
            value={appIconSize}
            label={t('settings.appIconSize')}
            onChange={(e) => setAppIconSize(e.target.value as 'large' | 'small')}
          >
            <MenuItem value="large">{t('settings.iconSizeLarge')}</MenuItem>
            <MenuItem value="small">{t('settings.iconSizeSmall')}</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Two-Factor Authentication Section — only shown when admin has enabled 2FA */}
      {config?.twoFactorEnabled && (
      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom>{t('twoFactor.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('twoFactor.description')}
        </Typography>

        {message2FA && (
          <Alert severity={message2FA.severity} sx={{ mb: 2 }} onClose={() => setMessage2FA(null)}>
            {message2FA.text}
          </Alert>
        )}

        {!enrolling && !twoFactorEnabled && (
          <Button variant="contained" onClick={handleStartEnroll}>
            {t('twoFactor.enable')}
          </Button>
        )}

        {!enrolling && twoFactorEnabled && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('twoFactor.enabled')}
            </Alert>
            {!disableConfirm ? (
              <Button variant="outlined" color="error" onClick={() => setDisableConfirm(true)}>
                {t('twoFactor.disable')}
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDisable2FA}
                  disabled={loading2FA}
                >
                  {loading2FA ? <CircularProgress size={20} color="inherit" /> : t('twoFactor.disableConfirm')}
                </Button>
                <Button variant="outlined" onClick={() => setDisableConfirm(false)}>
                  {t('twoFactor.disableCancel')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {enrolling && (
          <Box sx={{ mt: 2 }}>
            {enrollError && (
              <Alert severity="error" sx={{ mb: 2 }}>{enrollError}</Alert>
            )}

            {enrollOtpauth && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                  <QRCodeSVG value={enrollOtpauth} size={180} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  {t('twoFactor.scanQR')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                  {t('twoFactor.manualKey')}: {enrollSecret}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label={t('twoFactor.enterCode')}
              value={enrollCode}
              onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{
                mb: 2,
                '& input': { textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.3em' },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleConfirmEnroll}
                disabled={loading2FA || enrollCode.length !== 6}
              >
                {loading2FA ? <CircularProgress size={20} color="inherit" /> : t('twoFactor.verify')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setEnrolling(false)
                  setEnrollSecret('')
                  setEnrollOtpauth('')
                  setEnrollCode('')
                  setEnrollError('')
                }}
              >
                {t('twoFactor.cancel')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      )}
    </Box>
  )
}